import { collection, getDocs, query, where, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Campaign } from '../types/campaign';
import { getUserCompletedCampaigns } from './completedCampaignService';

/**
 * Fetch all active campaigns from Firestore
 * @returns Array of Campaign objects sorted by priority
 */
export const getAllCampaigns = async (): Promise<Campaign[]> => {
  try {
    const campaignsRef = collection(db, 'campaigns');
    const q = query(
      campaignsRef,
      where('isActive', '==', true),
      orderBy('priority', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    const campaigns: Campaign[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      campaigns.push({
        id: doc.id,
        ...data,
        // Convert Firestore Timestamp to Date if needed
        createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt)
      } as Campaign);
    });
    
    return campaigns;
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    return [];
  }
};

/**
 * Fetch campaigns filtered by category
 * @param category - The category to filter by
 * @returns Array of Campaign objects in the specified category
 */
export const getCampaignsByCategory = async (category: string): Promise<Campaign[]> => {
  try {
    const campaignsRef = collection(db, 'campaigns');
    const q = query(
      campaignsRef,
      where('isActive', '==', true),
      where('category', '==', category),
      orderBy('priority', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    const campaigns: Campaign[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      campaigns.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt)
      } as Campaign);
    });
    
    return campaigns;
  } catch (error) {
    console.error('Error fetching campaigns by category:', error);
    return [];
  }
};

/**
 * Fetch campaigns by main category (popular or anbefalet)
 * @param mainCategory - The main category to filter by ('popular' or 'anbefalet')
 * @returns Array of Campaign objects in the specified main category
 */
export const getCampaignsByMainCategory = async (mainCategory: 'popular' | 'anbefalet'): Promise<Campaign[]> => {
  try {
    const campaignsRef = collection(db, 'campaigns');
    const q = query(
      campaignsRef,
      where('isActive', '==', true),
      where('mainCategory', 'array-contains', mainCategory),
      orderBy('priority', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    const campaigns: Campaign[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      campaigns.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt)
      } as Campaign);
    });
    
    return campaigns;
  } catch (error) {
    console.error(`Error fetching campaigns by main category (${mainCategory}):`, error);
    return [];
  }
};

/**
 * Fetch popular campaigns (mainCategory contains 'popular')
 * @returns Array of popular Campaign objects sorted by priority
 */
export const getPopularCampaigns = async (): Promise<Campaign[]> => {
  return getCampaignsByMainCategory('popular');
};

/**
 * Fetch recommended campaigns (mainCategory contains 'anbefalet')
 * @returns Array of recommended Campaign objects sorted by priority
 */
export const getAnbefaletCampaigns = async (): Promise<Campaign[]> => {
  return getCampaignsByMainCategory('anbefalet');
};

/**
 * Fetch a single campaign by ID
 * @param campaignId - The campaign ID
 * @returns Campaign object or null if not found
 */
export const getCampaignById = async (campaignId: string): Promise<Campaign | null> => {
  try {
    const campaignDoc = await getDoc(doc(db, 'campaigns', campaignId));
    
    if (campaignDoc.exists()) {
      const data = campaignDoc.data();
      return {
        id: campaignDoc.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt)
      } as Campaign;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching campaign by ID:', error);
    return null;
  }
};

/**
 * Merge completed status with campaigns for a specific user
 * @param campaigns - Array of campaigns to merge with completed status
 * @param userId - The user ID to check completed campaigns for
 * @returns Array of campaigns with isCompleted flag set
 */
export const mergeCampaignsWithCompletedStatus = async (
  campaigns: Campaign[],
  userId: string | undefined
): Promise<Campaign[]> => {
  console.log('🔄 mergeCampaignsWithCompletedStatus called with:', {
    campaignCount: campaigns.length,
    userId,
    campaignIds: campaigns.map(c => c.id)
  });

  if (!userId) {
    console.log('⚠️ No userId provided, returning campaigns without completion status');
    return campaigns;
  }

  try {
    const completedCampaignIds = await getUserCompletedCampaigns(userId);
    console.log('🔄 Merging completed status. Completed campaignId fields:', completedCampaignIds);
    
    const mergedCampaigns = campaigns.map(campaign => {
      // Match against campaignId FIELD, not document ID
      const isCompleted = completedCampaignIds.includes(campaign.campaignId);
      console.log(`🔄 Campaign doc:${campaign.id} field:${campaign.campaignId} (${campaign.title}): isCompleted = ${isCompleted}`);
      return {
        ...campaign,
        isCompleted
      };
    });
    
    console.log('✅ Merge complete. Campaigns with completion status:', 
      mergedCampaigns.filter(c => c.isCompleted).length + '/' + mergedCampaigns.length
    );
    
    return mergedCampaigns;
  } catch (error) {
    console.error('❌ Error merging campaigns with completed status:', error);
    return campaigns;
  }
};

